import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Building2, UserCheck, Repeat, RefreshCw, Landmark, ShieldCheck, Boxes, LineChart, TrendingUp, Dice5, Banknote, Coins, Globe2, FileBadge, Plane, MessageCircle } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { CONTACT } from "@/lib/constants";
import { IMAGES } from "@/lib/images";
import { setSeo } from "@/lib/seo";

const GROUPS = [
  {
    key: "corporate", title: "Corporate Solutions",
    blurb: "Practical entity services for founders who need speed, privacy or continuity.",
    items: [
      { id: "shelf", icon: Building2, title: "Readymade (Shelf) Companies", body: "Established, compliant companies ready to transfer — ideal when you need an entity with existing registration history without waiting for a fresh formation." },
      { id: "nominee", icon: UserCheck, title: "Nominee Services", body: "Professional nominee director and shareholder arrangements to support privacy and local-presence requirements, fully documented and transparent to you as beneficial owner." },
      { id: "redomicile", icon: Repeat, title: "Re-Domiciliation", body: "Move your existing company to a new jurisdiction without dissolving it — preserving your track record, contracts and banking relationships." },
      { id: "agent", icon: RefreshCw, title: "Change of Registered Agent", body: "Transfer your registered agent smoothly while keeping your company in good standing and compliant with local filings." },
    ],
  },
  {
    key: "structures", title: "Wealth & Structures",
    blurb: "Vehicles for asset protection, succession and pooled investment.",
    items: [
      { id: "foundation", icon: ShieldCheck, title: "Foundations", body: "Private foundations for asset protection, succession and philanthropic planning, with clear governance and long-term continuity." },
      { id: "trust", icon: Landmark, title: "Trusts", body: "International trust structures for estate planning and safeguarding assets across generations, arranged with reputable trustees." },
      { id: "dao", icon: Boxes, title: "DAO Structuring", body: "Legal wrappers and advisory for decentralised autonomous organisations, aligning on-chain governance with a recognised legal entity." },
      { id: "fund", icon: LineChart, title: "Investment Funds", body: "Formation and administration support for private and regulated investment funds, from structuring to service-provider selection." },
    ],
  },
  {
    key: "licensing", title: "Licensing & Regulatory Advisory",
    blurb: "Application assistance and compliance guidance across supported frameworks.",
    items: [
      { id: "forex", icon: TrendingUp, title: "Forex Broker Licensing", body: "Advisory and application preparation for brokerage authorisations in supported jurisdictions, including documentation and process management." },
      { id: "gaming", icon: Dice5, title: "Gaming & Betting Licensing", body: "Consulting and application support for operators seeking licences under regulated online-gaming frameworks, with an emphasis on compliance." },
      { id: "banklicense", icon: Banknote, title: "Bank & EMI Licensing", body: "Advisory for banking and electronic-money-institution authorisations, covering business planning, structuring and regulatory liaison." },
      { id: "vasp", icon: Coins, title: "VASP Licensing", body: "Virtual Asset Service Provider registration and compliance advisory for crypto and digital-asset businesses." },
      { id: "vara", icon: Globe2, title: "VARA Licensing (Dubai)", body: "Guidance through Dubai's Virtual Assets Regulatory Authority approval process, from readiness assessment to submission." },
    ],
  },
  {
    key: "individuals", title: "Individuals & US Services",
    blurb: "Tax identifiers and mobility solutions for founders and their businesses.",
    items: [
      { id: "einitin", icon: FileBadge, title: "EIN & ITIN Services", body: "Obtain US Employer Identification Numbers and Individual Taxpayer Identification Numbers for your company and personal filing needs." },
      { id: "citizenship", icon: Plane, title: "Citizenship & Residency", body: "Advisory on citizenship-by-investment and residency programmes worldwide, matched to your mobility and planning goals." },
    ],
  },
];

const GROUP_TINT = {
  corporate: "text-orange-600 bg-orange-50 border-orange-100",
  structures: "text-primary bg-secondary border-border",
  licensing: "text-orange-600 bg-orange-50 border-orange-100",
  individuals: "text-primary bg-secondary border-border",
};

export default function MoreServicesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  useEffect(() => {
    setSeo({ title: `${t("nav.moreServices")} — ${t("more.h1", { defaultValue: "Beyond incorporation." })} | UNICORP`, description: t("more.lead", { defaultValue: "A full spectrum of corporate, wealth, regulatory and individual services — delivered by specialists who guide every step." }).slice(0, 158) });
  }, [t]);

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 200);
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="relative grain pt-36 pb-16 border-b border-border bg-secondary/40">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="text-xs uppercase tracking-[0.2em] text-orange-600">{t("nav.moreServices")}</p>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight text-primary">{t("more.h1", { defaultValue: "Beyond incorporation." })}</h1>
            <p className="mt-5 text-muted-foreground text-lg leading-relaxed max-w-2xl">
              {t("more.lead", { defaultValue: "A full spectrum of corporate, wealth, regulatory and individual services — delivered by specialists who guide every step. Explore the areas below and talk to us about your goals." })}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button size="lg" className="rounded-sm bg-orange-500 text-white hover:bg-orange-600 h-12 px-7" onClick={() => navigate("/contact")} data-testid="more-hero-cta">{t("more.heroCta", { defaultValue: "Talk to a specialist" })} <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
              <Button size="lg" variant="outline" className="rounded-sm border-slate-300 bg-card hover:bg-secondary h-12 px-7" onClick={() => navigate("/company-formation")} data-testid="more-hero-services">{t("nav.companyFormation")}</Button>
            </div>
          </motion.div>
        </div>
      </section>

      {GROUPS.map((g) => (
        <section key={g.key} className="max-w-7xl mx-auto px-5 sm:px-8 py-14 border-b border-border/60 last:border-0">
          <div className="max-w-2xl">
            <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-primary">{t(`more.group.${g.key}.title`, { defaultValue: g.title })}</h2>
            <p className="mt-2 text-muted-foreground">{t(`nav.svcBlurb.${g.key}`, { defaultValue: g.blurb })}</p>
          </div>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {g.items.map((it, i) => (
              <motion.div key={it.id} id={it.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.04 }}
                className="group scroll-mt-28 rounded-2xl border border-border bg-card p-6 card-soft hover:border-orange-300 hover:-translate-y-0.5 transition-all" data-testid={`service-${it.id}`}>
                <span className={`grid place-items-center h-11 w-11 rounded-sm border ${GROUP_TINT[g.key]}`}><it.icon className="h-5 w-5" /></span>
                <h3 className="mt-4 font-heading text-lg text-primary">{t(`more.item.${it.id}.title`, { defaultValue: it.title })}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t(`more.item.${it.id}.body`, { defaultValue: it.body })}</p>
                <button onClick={() => navigate("/contact")} className="mt-4 inline-flex items-center gap-1 text-sm text-orange-600 hover:text-orange-700 transition-colors" data-testid={`service-cta-${it.id}`}>
                  {t("more.enquire", { defaultValue: "Enquire" })} <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </motion.div>
            ))}
          </div>
        </section>
      ))}

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="rounded-2xl bg-primary text-primary-foreground px-8 py-14 grain grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight">{t("more.unsure", { defaultValue: "Not sure which service fits?" })}</h2>
            <p className="mt-3 text-primary-foreground/80 leading-relaxed max-w-md">{t("more.unsureBody", { defaultValue: "Share your goals and our advisors will recommend the right structure and roadmap — no obligation." })}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button size="lg" className="rounded-sm bg-orange-500 text-white hover:bg-orange-600 h-12 px-7" onClick={() => navigate("/contact")} data-testid="more-cta-contact">{t("more.contactTeam", { defaultValue: "Contact our team" })} <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
              <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-sm bg-[#25D366] text-white h-12 px-6 hover:brightness-95 transition" data-testid="more-cta-whatsapp"><MessageCircle className="h-5 w-5" /> WhatsApp</a>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden ring-1 ring-white/10 hidden lg:block"><img src={IMAGES.trust} alt="Advisory" className="w-full h-56 object-cover" /></div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
